import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import FaqAccordion from "@/components/feature/FaqAccordion";
import usePageMeta from "@/hooks/usePageMeta";
import { getArticle, getRelatedArticles } from "@/mocks/articles";
import { faqs } from "@/mocks/faqs";
import { categories } from "@/mocks/categories";

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticle(slug) : undefined;

  usePageMeta({
    title: article ? `${article.title} - 可游漫画网` : "文章详情 - 可游漫画网",
    description: article ? article.excerpt : "游戏资讯与联动情报相关内容，可游漫画网。",
  });

  if (!article) {
    return (
      <PageLayout>
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-file-search-line text-5xl text-foreground-300" />
          <h1 className="mt-4 font-heading text-2xl text-foreground-950">没有找到这篇文章</h1>
          <p className="mt-2 text-foreground-600">内容可能已被移动或下架。</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 hover:bg-primary-400"
          >
            返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const related = getRelatedArticles(article);
  const cat = categories.find((c) => c.slug === article.categorySlug);

  return (
    <PageLayout>
      <article className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: article.category, to: `/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <header>
              <span className="manga-clip mb-4 inline-flex items-center gap-1 bg-primary-500 px-3 py-1 text-sm font-bold text-background-50">
                <i className="ri-focus-3-line" />
                {article.tag}
              </span>
              <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5"><i className="ri-user-line" />{article.author}</span>
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line" />{article.date}</span>
                <span className="flex items-center gap-1.5"><i className="ri-eye-line" />{article.views.toLocaleString()} 阅读</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line" />{article.duration}</span>
              </div>
            </header>

            <div className="mt-6 overflow-hidden rounded-lg">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 可游漫画网`}
                className="h-64 w-full object-cover object-top md:h-96"
              />
            </div>

            <div className="mt-6 text-base leading-8 text-foreground-700">
              <p className="mb-4 font-medium text-foreground-900">{article.excerpt}</p>
              <p className="mb-4">
                随着游戏行业与动画、漫画领域的跨界越来越紧密，官方改编漫画已经成为新游宣发和版本更新的重要一环。{article.category}
                的这则消息，正体现了当下热门游戏漫画化与联动内容的新趋势，也印证了玩家对游戏剧情延伸阅读的旺盛需求。
              </p>
              <h2 className="mt-8 mb-3 font-heading text-xl text-foreground-950">一、企划背景与官方动态</h2>
              <p className="mb-4">
                从官方公布的资料来看，本次企划不仅包含联动角色与专属玩法，还配套上线了主题漫画内容。官方在公告中强调，希望通过漫画叙事进一步丰富游戏世界观，
                让玩家在游戏之外也能沉浸式地体验剧情。这也延续了近期新游漫画化、版本更新漫画的创作方向，将文字与画面相结合。
              </p>
              <h2 className="mt-8 mb-3 font-heading text-xl text-foreground-950">二、活动漫画与版本更新内容</h2>
              <p className="mb-4">
                在联动活动期间，玩家可以通过完成每日任务与活动关卡解锁漫画剧情页，随着版本更新漫画的逐步放送，相关隐藏剧情与奖励线索也会陆续公开。
                可游漫画网将持续跟进活动漫画内容，第一时间整理剧情脉络、彩蛋解析与奖励获取建议。
              </p>
              <p className="mb-4">
                对于喜欢剧情向内容的玩家来说，这类活动漫画不仅是游戏信息的补充，更是一种独特的阅读体验。我们建议各位玩家关注官方活动时间表，
                合理安排活动参与节奏，以免错过限定的联动漫画与纪念奖励。
              </p>
              <h2 className="mt-8 mb-3 font-heading text-xl text-foreground-950">三、后续看点与结语</h2>
              <p className="mb-4">
                本次消息的意义，不仅在于单次活动的上线，更预示着游戏与漫画内容融合的持续深化。作为聚焦游戏资讯与联动情报的内容平台，
                可游漫画网会继续为大家带来新游漫画化、版本更新漫画与活动漫画的一手报道与深度解读。
              </p>
              <p>
                如果你对本次{article.tag}相关内容还有疑问，欢迎通过投稿咨询页面与我们交流，或持续关注可游漫画网的后续更新。
              </p>
            </div>

            {cat && (
              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
                <span className="text-sm text-foreground-500">更多相关：</span>
                <Link
                  to={`/${cat.slug}`}
                  className="whitespace-nowrap rounded-md bg-background-100 px-3 py-1 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  {cat.name}
                </Link>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg text-foreground-950">目录导航</h2>
              <ul className="mt-4 space-y-3 text-sm">
                <li><a href="#bg" className="flex items-center gap-2 text-foreground-700 hover:text-primary-600"><i className="ri-hashtag text-primary-500" />一、企划背景与官方动态</a></li>
                <li><a href="#update" className="flex items-center gap-2 text-foreground-700 hover:text-primary-600"><i className="ri-hashtag text-accent-500" />二、活动漫画与版本更新内容</a></li>
                <li><a href="#look" className="flex items-center gap-2 text-foreground-700 hover:text-primary-600"><i className="ri-hashtag text-secondary-500" />三、后续看点与结语</a></li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-3">
                <span className="manga-clip-both flex h-12 w-12 items-center justify-center bg-accent-500 text-lg text-foreground-950">
                  <i className="ri-pencil-ruler-line" />
                </span>
                <div>
                  <h2 className="font-heading text-base text-foreground-950">{article.author}</h2>
                  <p className="text-xs text-foreground-500">可游漫画网内容编辑</p>
                </div>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                专注游戏资讯、新游漫画化与联动情报整理，努力为大家带来准确、及时、有温度的游戏漫画内容。
              </p>
            </div>

            <div className="rounded-lg bg-background-900 p-5 text-background-100">
              <h2 className="font-heading text-lg text-background-50">情报订阅</h2>
              <p className="mt-2 text-sm text-background-300">关注可游漫画网，不错过任何一条联动情报与漫画动态。</p>
              <Link
                to="/contact"
                className="manga-clip mt-4 inline-flex items-center gap-1.5 whitespace-nowrap bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 hover:bg-primary-400"
              >
                联系我们 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-12 border-t border-background-200 pt-8">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">相关推荐</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </section>

        <section className="mt-12 border-t border-background-200 pt-8">
          <h2 className="mb-6 font-heading text-xl text-foreground-950 md:text-2xl">你可能还想了解</h2>
          <FaqAccordion items={faqs.slice(0, 4)} />
        </section>
      </article>
      <BackToTop />
    </PageLayout>
  );
}